import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faLinkSlash } from '@fortawesome/pro-light-svg-icons/faLinkSlash';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { errorAlertMessage } from '../../common/views/ErrorAlert/errorAlertMessage';
import { ConfirmDialog } from '../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';

interface RemoveCatalogItemModalProps {
  item?: CatalogItem;
  closeModal: () => void;
  onConfirm: (itemUuid: string) => Promise<boolean>;
  isLoading: boolean;
  error?: ErrorAPIResult['error'];
}

/**
 * The confirmation in front of an un-claim (design dialogs a1).
 *
 * Only the catalog row goes. Any page built from the same release stays online:
 * the artist put that url into the world, and tidying this screen must not
 * break it.
 */
const RemoveCatalogItemModalComponent: React.FC<RemoveCatalogItemModalProps> = ({
  item,
  closeModal,
  onConfirm,
  isLoading,
  error,
}) => {
  const intl = useIntl();

  const confirm = useCallback(() => {
    if (item === undefined) {
      return;
    }
    onConfirm(item.uuid)
      .then((removed) => {
        if (!removed) {
          return;
        }
        closeModal();
      })
      .catch(console.error);
  }, [closeModal, item, onConfirm]);

  const isClaimed = item?.kind === CatalogItemKind.claimed;

  return (
    <ConfirmDialog
      isOpen={item !== undefined}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={isClaimed ? faLinkSlash : faTrash} />}
      title={
        isClaimed
          ? intl.formatMessage({ id: 'app.sml.catalog.remove.title_claim', defaultMessage: 'Un-claim this release' })
          : intl.formatMessage({ id: 'app.sml.catalog.remove.title', defaultMessage: 'Remove from the catalog' })
      }
      description={intl.formatMessage(
        {
          id: 'app.sml.catalog.remove.help',
          defaultMessage:
            '“{title}” leaves your catalog and stops appearing when you build a smartlink. Any page you already built from it stays online. You can claim it again at any time.',
        },
        { title: item?.title ?? '' }
      )}
      confirmLabel={intl.formatMessage({ id: 'app.sml.catalog.remove.confirm', defaultMessage: 'Remove it' })}
      isBusy={isLoading}
      error={error === undefined ? undefined : errorAlertMessage(intl, error)}
      onConfirm={confirm}
      onCancel={closeModal}
      testId="catalog-remove-dialog"
    />
  );
};

export const RemoveCatalogItemModal = TypedMemo(RemoveCatalogItemModalComponent);
